import * as React from "react";
import { useTranslation } from "react-i18next";

import { Generic } from "src/@types/Category";

import agent from "src/helpers/apiAgent";
import { successHandler, errorHandler } from "src/helpers/responseHandler";

import { PROTECTED_ROUTES } from "src/constants/Navigation";

import useRoutes from "src/hooks/useRoutes";
import useCourse from "src/hooks/useCourse";
import useNewAssignment from "src/hooks/useNewAssignment";
import { languageConfig } from "src/screens/Questions/AddBulkQuestions";

const MAX_CONTEXT_SIZE = 3000;
const MIN_CONTEXT_SIZE = 50;

// Set defaults for reuse
const DEFAULTS = {
  loading: false,
  questionData: {
    keywords: [],
    courseId: "",
    questionType: 1,
    questionCategory: 1,
    numberOfQuestions: 1,
    questionDificulty: 1,
    // bloomsTaxonomy: "create",
    instructions: "",
    contexts: [""],
    gradeId: "",
  },
  lectures: [],
  activeStep: 0,
  topicLectures: [],
  selectedLecture: null,
  generatedQuestions: [],
  subject: { id: "", name: "", courseId: "", courseName: "" },
  onNext: () => {},
  onCancel: () => {},
  resetQuestionData: () => {},
  onSubmitQuestions: async () => {},
  onGenerateQuestions: async () => {},
  setActiveStep: (step: Generic) => {},
  setSubject: (subject: Generic) => {},
  onRegenerateQuestions: async () => {},
  setQuestionData: (data: Generic) => {},
  addQuestionTags: (value: string) => {},
  setTopicLectures: (data: Generic) => {},
  removeQuestionTag: (value: string) => {},
  setSelectedLecture: (lecture: Generic) => {},
  handleGeneratedDelete: (data: Generic) => {},
  handleRegenerateQuestion: (data: Generic) => {},
  handleEditGeneratedQuestion: (data: Generic, index: number) => {},
  //   editQuestion: (data: Generic, subject: Generic) => {},
};

const BulkQuestionContext = React.createContext(DEFAULTS);

const BulkQuestionProvider: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
}) => {
  const { t, i18n } = useTranslation();
  const { navigateTo } = useRoutes();
  const { activeGrade } = useCourse();
  const { setRefetch } = useNewAssignment();

  const [loading, setLoading] = React.useState(DEFAULTS.loading);
  const [subject, setSubject] = React.useState(DEFAULTS?.subject);
  const [lectures, setLectures] = React.useState(DEFAULTS?.lectures);
  const [activeStep, setActiveStep] = React.useState(DEFAULTS.activeStep);
  const [questionData, setQuestionData] = React.useState(DEFAULTS.questionData);
  const [topicLectures, setTopicLectures] = React.useState(
    DEFAULTS.topicLectures
  );
  const [selectedLecture, setSelectedLecture] = React.useState(
    DEFAULTS.selectedLecture
  );
  const [generatedQuestions, setGeneratedQuestions] = React.useState(
    DEFAULTS.generatedQuestions
  );

  React.useEffect(() => {
    if (activeGrade?.id) {
      setQuestionData((prev) => ({ ...prev, gradeId: activeGrade?.id }));
    }
  }, [activeGrade]);

  const resetQuestionData = () => setQuestionData(DEFAULTS?.questionData);

  //   const editQuestion = (data, subject) => {
  //     const extractedData = {};
  //     Object.keys(DEFAULTS?.questionData).forEach(
  //       (key) => (extractedData[key] = data[key] ?? DEFAULTS.questionData[key])
  //     );
  //     // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //     // @ts-ignore
  //     setQuestionData(extractedData);
  //     navigateTo(PROTECTED_ROUTES.ADD_QUESTIONS, { state: { subject } });
  //   };

  const addQuestionTags = (val: string) => {
    // const index = questionData?.keywords.indexOf(val, 0);
    // if (index > -1) {
    //   const updated = [...questionData?.keywords];
    //   updated.splice(index, 1);
    //   setQuestionData((prev) => ({ ...prev, keywords: updated }));
    // } else {
    //   setQuestionData((prev) => ({
    //     ...prev,
    //     keywords: [...questionData?.keywords, val],
    //   }));
    // }
    setQuestionData((prev) => ({ ...prev, keywords: [val] }));
  };

  const removeQuestionTag = (val: string) => {
    // const index = questionData?.keywords.indexOf(val, 0);
    // if (index > -1) {
    //   const updated = [...questionData?.keywords];
    //   updated.splice(index, 1);
    //   setQuestionData((prev) => ({ ...prev, tags: updated }));
    // }
    setQuestionData((prev) => ({ ...prev, keywords: [] }));
  };

  const handleGeneratedDelete = (question) => {
    const index = generatedQuestions?.findIndex(
      (gq) =>
        gq?.questionNo === question?.questionNo &&
        gq?.questionText === question?.questionText
    );

    if (index > -1) {
      const updated = [...generatedQuestions];
      updated.splice(index, 1);
      setGeneratedQuestions(updated);
    }
  };

  const handleRegenerateQuestion = async (question) => {
    try {
      // checking data validity
      if (isQuestionDataValidForGenerating()) {
        setLoading(true);

        let response;
        if (languageConfig[i18n?.language]?.callDifferentApi) {
          response = await agent.Questions.bulkRgenerateQuestionsTurkish({
            ...{ ...questionData, numberOfQuestions: 1 },
            questions: [question],
            gradeId: activeGrade?.id,
          });
        } else {
          response = await agent.Questions.bulkRgenerateQuestions({
            ...{ ...questionData, numberOfQuestions: 1 },
            questions: [question],
            gradeId: activeGrade?.id,
          });
        }

        if (response?.code === 200) {
          const copyOfQuestions = JSON.parse(
            JSON.stringify(generatedQuestions)
          );
          for (let i = 0; i < copyOfQuestions.length; i++) {
            if (
              copyOfQuestions[i].questionNo === question?.questionNo &&
              copyOfQuestions[i].questionText === question?.questionText
            ) {
              if (response?.data?.length) {
                copyOfQuestions[i] = { ...response?.data?.[0] };
              } else {
                copyOfQuestions[i] = { ...response?.data };
              }
              break;
            }
          }
          setGeneratedQuestions(copyOfQuestions);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const isQuestionDataValidForGenerating = () => {
    if (
      questionData?.numberOfQuestions > 5 ||
      questionData?.numberOfQuestions < 1
    ) {
      errorHandler(t("errors.noOfQuestions"));
      return false;
    }
    if (!questionData?.courseId) {
      errorHandler(t("errors.courseMissing"));
      return false;
    }
    if (!questionData?.questionType) {
      errorHandler(t("errors.questionType"));
      return false;
    }
    if (!questionData?.questionCategory) {
      errorHandler(t("errors.questionBank"));
      return false;
    }
    if (questionData?.keywords?.length === 0) {
      errorHandler(t("errors.questionsAITags"));
      return false;
    }
    // if (!questionData?.bloomsTaxonomy) {
    //   errorHandler(t("errors.questionBloomsTaxanomy"));
    //   return false;
    // }
    if (!questionData?.questionDificulty) {
      errorHandler(t("errors.questionsDifficultyLevel"));
      return false;
    }
    const trimmedContexts = (questionData?.contexts || [])
      .map((ctx) => ctx.trim())
      .filter((ctx) => ctx !== "");

    const totalContextLength = trimmedContexts.join("").length;

    if (totalContextLength > MAX_CONTEXT_SIZE) {
      errorHandler(t("errors.contextMoreThan3000"));
      return false;
    }
    if (totalContextLength > 0 && totalContextLength < MIN_CONTEXT_SIZE) {
      errorHandler(t("errors.contextLessThan50"));
      return false;
    }

    return true;
  };

  const onSubmitQuestions = async () => {
    // Making decision to update or create question
    try {
      // checking data validity
      setLoading(true);
      const response = await agent.Questions.bulkCreateQuestions(
        generatedQuestions
      );

      if (response.code === 201) {
        successHandler(response);
        setRefetch((prev) => !prev);
        navigateTo(PROTECTED_ROUTES.QUESTIONS_LISTING, {
          state: { subject },
        });
        onCancel();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onGenerateQuestions = async () => {
    try {
      // checking data validity
      if (isQuestionDataValidForGenerating()) {
        setLoading(true);

        // Making sure grade id always exists
        if (!questionData?.gradeId) {
          questionData.gradeId = activeGrade?.id;
        }

        let response;
        if (languageConfig[i18n?.language]?.callDifferentApi) {
          // Removing contexts from question data
          delete questionData.contexts;

          response = await agent.Questions.bulkGenerateQuestionsTurkish(
            questionData,
            i18n.language
          );
        } else {
          response = await agent.Questions.bulkGenerateQuestions(
            questionData,
            i18n.language
          );
        }

        if (response?.code === 200) {
          if (response?.data?.length === 0) {
            errorHandler(t("errors.errorBulkGenerate"));
            return;
          }
          // Adding question bank details to each question
          const generatedQuestionsWithCategory = response?.data?.map((bq) => ({
            ...bq,
            questionCategory: questionData?.questionCategory,
          }));
          setGeneratedQuestions(generatedQuestionsWithCategory);
          navigateTo(PROTECTED_ROUTES.BULK_QUESTIONS_LISTING, {
            state: { subject },
          });
          setActiveStep(0);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onRegenerateQuestions = async () => {
    try {
      // checking data validity
      if (isQuestionDataValidForGenerating()) {
        setLoading(true);

        let response;
        if (languageConfig[i18n?.language]?.callDifferentApi) {
          response = await agent.Questions.bulkRgenerateQuestionsTurkish(
            {
              ...questionData,
              questions: generatedQuestions,
              gradeId: activeGrade?.id,
            },
            i18n.language
          );
        } else {
          response = await agent.Questions.bulkRgenerateQuestions(
            {
              ...questionData,
              questions: generatedQuestions,
              gradeId: activeGrade?.id,
            },
            i18n.language
          );
        }

        if (response?.code === 200) {
          setGeneratedQuestions(response?.data);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    resetQuestionData();
    navigateTo(PROTECTED_ROUTES.QUESTIONS_LISTING, { state: { subject } });
  };

  const onNext = () => {
    if (questionData?.keywords?.length) {
      setActiveStep(1);
    } else {
      errorHandler(t("errors.topicSelection"));
    }
  };

  const handleEditGeneratedQuestion = (updatedQuestion, index) => {
    const copyGeneratedQuestions = JSON.parse(
      JSON.stringify(generatedQuestions)
    );
    if (index > -1) {
      copyGeneratedQuestions[index] = updatedQuestion;
      setGeneratedQuestions(copyGeneratedQuestions);
      navigateTo(-1);
    }
  };

  const getLecturesByCourseId = async () => {
    try {
      setLoading(true);
      const response = await agent.Course.getLecturesByCourseId(subject?.id);

      if (response.code === 200) {
        setLectures(response?.data);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (subject?.id) {
      getLecturesByCourseId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);

  const contextValues = {
    loading,
    subject,
    onNext,
    onCancel,
    lectures,
    activeStep,
    setSubject,
    topicLectures,
    questionData,
    setActiveStep,
    selectedLecture,
    // editQuestion,
    addQuestionTags,
    setQuestionData,
    setTopicLectures,
    onSubmitQuestions,
    resetQuestionData,
    removeQuestionTag,
    generatedQuestions,
    setSelectedLecture,
    onGenerateQuestions,
    onRegenerateQuestions,
    handleGeneratedDelete,
    handleRegenerateQuestion,
    handleEditGeneratedQuestion,
  };

  return (
    <BulkQuestionContext.Provider value={contextValues}>
      {children}
    </BulkQuestionContext.Provider>
  );
};

export { BulkQuestionContext };
export default BulkQuestionProvider;
